import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Button, IconButton, MenuItem, Stack, Switch, SxProps, TextField, Tooltip, Typography } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DisplayValue, Drawer, MenuItemStateProps } from '@src/components';
import { DateHelper, ProjectModel } from '@src/core';
import { useTheme } from '@src/themes';
import { ptBR as ptBr } from 'date-fns/locale/pt-BR';
import { FC, Fragment, useEffect, useState } from 'react';
import { useSessionContext } from '../authentication';
import { useProjectContext } from './project.context';

type SwitchProjectMenuProps = MenuItemStateProps & {
  viewport: 'phone' | 'tablet' | 'desktop';
};

const SwitchProjectMenu: FC<SwitchProjectMenuProps> = ({ isMini, viewport }) => {
  const { COLORS } = useTheme();
  const {
    projects,
    projectName,
    activeEnvironmentProject,
    projectId,
    projectIsProduction,
    setReferenceDateOverride,
    referenceDateOverride,
    referenceDate,
    cognitoMode,
    setCognitoMode,
  } = useProjectContext();
  const { isAdmin } = useSessionContext();

  // modal state
  const [showModal, displayModal] = useState<boolean>(false);

  // projects state
  const [selectedProject, selectProject] = useState<string>(projectId);

  useEffect(() => {
    selectProject(projectId);
  }, [projectId]);
  // end: projects state

  // reference date state
  const [useTodayAsReference, setUseTodayAsReference] = useState<boolean>(referenceDateOverride === null);
  const [selectedReferenceDate, selectReferenceDate] = useState<Date>(referenceDate);

  useEffect(() => {
    setUseTodayAsReference(referenceDateOverride === null);
    selectReferenceDate(referenceDate);
  }, [referenceDateOverride, referenceDate]);
  // end: reference date state

  // cognito mode
  const [cognitoModeState, setCognitoModeState] = useState<boolean>(cognitoMode);

  useEffect(() => {
    setCognitoModeState(cognitoMode);
  }, [cognitoMode]);
  // end: cognito mode

  const confirmHandle = () => {
    /*
      A data de referência não atravessa projetos: ela descreve um recorte de
      um estudo, e carregá-la para outro faria o próximo abrir num passado que
      ninguém pediu.

      A string vazia é o item "Selecione": com ela activeEnvironmentProject dá
      early return e o projeto não muda, então não há troca que justifique
      descartar a data escolhida.
    */
    const projectChanged = selectedProject !== '' && selectedProject !== projectId;

    activeEnvironmentProject(selectedProject);
    setReferenceDateOverride(useTodayAsReference || projectChanged ? null : selectedReferenceDate);

    /*
      Repor o par local à mão porque o React ignora setState com o mesmo valor:
      se o override já era null, o efeito de sincronia acima não roda e o drawer
      reabriria mostrando "switch desligado + data antiga" enquanto o app já
      opera em hoje -- e um segundo Confirmar aplicaria aquela data.
    */
    if (projectChanged) {
      setUseTodayAsReference(true);
      selectReferenceDate(DateHelper.getDateBRTimezone());
    }

    setCognitoMode(cognitoModeState);
    displayModal(false);
  };

  const renderProjectInfo = (sx: SxProps, infoTextSize: string, infoColor?: string, showButton: boolean = true) => (
    <Stack padding={1} sx={{ ...sx, alignItems: 'center' }} direction={'row'} display={'flex'}>
      <Stack>
        <DisplayValue
          loading={false}
          width={100}
          label={'Projeto:'}
          value={`${projectName}`}
          direction='column'
          labelVariant='h5'
          valueVariant='body1'
          spacing={1 / 2}
          fontSize={infoTextSize}
          color={infoColor}
          sxValue={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}
        />
      </Stack>
      {showButton && projects.length > 1 && (
        <Stack>
          <Tooltip title={'Trocar de projeto'} placement={'right'} disableHoverListener={isMini}>
            <IconButton
              size='small'
              aria-label='account of current user'
              aria-controls='menu-appbar'
              aria-haspopup='true'
              onClick={() => displayModal(true)}
            >
              <SettingsOutlinedIcon
                sx={{
                  fontSize: 24,
                  ...(infoColor ? { color: infoColor } : {}),
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      <Drawer
        opened={showModal}
        handleClose={(v: boolean) => displayModal(v)}
        title={'Trocar de projeto'}
        actions={
          <Button variant='contained' startIcon={<CheckIcon />} onClick={() => confirmHandle()} color={'primary'}>
            Confirmar
          </Button>
        }
      >
        <Stack spacing={2} gap={1} direction={'column'} display={'flex'} width={'100%'}>
          <TextField
            select
            variant='filled'
            autoComplete='off'
            label={'Projeto'}
            value={selectedProject}
            onChange={(event: any) => {
              selectProject(event.target.value);
            }}
          >
            <MenuItem value={''}>Selecione</MenuItem>
            {projects.map((project: ProjectModel, key: number) => (
              <MenuItem value={project.id} key={key}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>

          {/*
            Operar numa data que não é hoje é ferramenta de administração: muda
            o que o app inteiro entende por "hoje" e, enquanto está ativa,
            bloqueia toda escrita. Quem não é admin não vê o controle -- e o
            ProjectProvider garante que também não herde uma data ligada.
          */}
          {isAdmin && (
            <Fragment>
              <Stack display={'flex'} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant='body1'>Usar data de hoje</Typography>
                <Switch
                  checked={useTodayAsReference}
                  onChange={(_event, checked) => {
                    setUseTodayAsReference(checked);
                  }}
                />
              </Stack>

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBr}>
                <DesktopDatePicker
                  label='Data de referência'
                  format='dd/MM/yyyy'
                  value={selectedReferenceDate}
                  disabled={useTodayAsReference}
                  onChange={date => {
                    if (date !== null) selectReferenceDate(date);
                  }}
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params: any) => (
                      <TextField variant='filled' {...params} sx={{ marginBottom: 1 }} required={false} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Fragment>
          )}

          <Stack display={'flex'} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant='body1'>Ativar modo cognito</Typography>
            <Switch
              checked={cognitoModeState}
              onChange={(_event, checked) => {
                setCognitoModeState(checked);
              }}
            />
          </Stack>
        </Stack>
      </Drawer>

      {!isMini &&
        ['desktop', 'phone'].includes(viewport) &&
        renderProjectInfo(
          {
            backgroundColor: projectIsProduction ? COLORS.others.grayLight : COLORS.others.redLight,
            justifyContent: 'space-between',
            overflowX: 'hidden',
          },
          '12px'
        )}

      {(isMini || ['tablet'].includes(viewport)) && (
        <Stack
          padding={1}
          sx={{
            justifyContent: 'center',
            backgroundColor: projectIsProduction ? COLORS.others.grayLight : COLORS.others.redLight,
          }}
          direction={'row'}
          display={'flex'}
        >
          <Tooltip title={renderProjectInfo({}, '14px', COLORS.others.gray, false)} placement={'right'}>
            <IconButton
              size='small'
              aria-label='account of current user'
              aria-controls='menu-appbar'
              aria-haspopup='true'
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 32,
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </>
  );
};

export { SwitchProjectMenu };
